<template>
  <div class="income-verification-tile-wrapper">
    <tile class="income-verification-tile">
      <div class="row income-verification-tile-header">
        <div class="col col-left">
          <div class="title">
            <span>{{ fullName }}&nbsp;Income Verification</span>
            <p
              v-if="
                (selectedSources?.length === 0 && showIncomeUploadSection) ||
                (editSelection && selectedSources?.length > 0)
              "
              class="caption"
            >
              To expedite the process, let's get a little information. Please select all income
              types you've received in the past two years.
            </p>
          </div>
        </div>
        <div class="col col-right">
          <div class="action">
            <a @click="toggleIncomeSelector" class="title action">
              {{
                !showIncomeUploadSection
                  ? "Edit&nbsp;"
                  : editSelection ||
                    (selectedIncomeSources?.length === 0 && showIncomeUploadSection)
                  ? "Cancel"
                  : "Close"
              }}
              <font-awesome-icon
                v-if="!showIncomeUploadSection"
                class="icon"
                :icon="['fas', 'fa-pen']"
            /></a>
          </div>
        </div>
      </div>
      <div class="income-verification-tile-content" v-show="!gettingData">
        <next-button
          class="w-100"
          v-show="!showIncomeUploadSection"
          @click="showIncomeUploadSection = !showIncomeUploadSection"
          :text="'Continue'"
        />
        <div
          v-if="
            (selectedIncomeSources?.length === 0 && showIncomeUploadSection) ||
            (editSelection && selectedIncomeSources?.length > 0 && showIncomeUploadSection)
          "
        >
          <div class="income-source-wrapper" v-for="option in incomeSources" :key="option.name">
            <base-form-field
              v-model="info[option?.name]"
              :schema="checkboxfieldSchema[option?.name]"
              @update:model-value="handleIncomeSourceSelect($event, option)"
            />
            <div
              class="employment-survey-questions-wrapper"
              v-show="
                showEmploymentSurveyQuestions &&
                checkboxfieldSchema[option?.name]?.label === 'Paycheck from an Employer'
              "
            >
              <base-form-field
                v-model="employmentStatusResponse"
                :schema="employmentStatusSchema"
                :options="employmentStatusOptions"
              />
              <base-form-field
                v-show="employmentStatusResponse === 'Yes'"
                v-model="employerNameResponse"
                :schema="employerNameSchema"
              />
            </div>
          </div>

          <div>
            Alimony, child support, or separate maintenance income need not be revealed if you do
            not wish to have it considered as a basis for repaying this obligation.
          </div>
          <next-button
            class="btn-primary"
            @click="updateIncomeSources"
            :text="`Continue`"
            :disabled="updatingIncomeSources"
          />
          <update-income-sources-error v-show="updateIncomeSourcesError" />
        </div>
        <div
          class="selected-income-sources"
          v-if="selectedIncomeSources?.length > 0 && showIncomeUploadSection && !editSelection"
        >
          <p class="selected-income-sources-title">Selected income sources</p>
          <ul class="selected-options">
            <li
              class="selected-option"
              v-for="(selectedOption, index) in selectedIncomeSources"
              :key="index"
            >
              {{ selectedOption.title }}
            </li>
          </ul>
          <a @click="resetSelection">edit income sources</a>
        </div>
      </div>
    </tile>
  </div>
</template>
<script setup lang="ts">
import { ref, reactive, onMounted, computed, watch } from "vue";
import type { PropType } from "vue";
import { useStore } from "vuex";
import { useRoute } from "vue-router";
import type { Application, Applicant, SurveyResponse } from "@/models/application";
import type { ApplicantIncomeSourcesRequest } from "@/models/api";
import type { FieldSchema, Option } from "@/models/form";
import type { SurveyResponseViewModel } from "@/models/viewModels";
import { IncomeSourceTypeIds, ProductIds, SurveyQuestionTypeIds } from "@/config/constants";
import { $api } from "@/services/api";
import BaseFormField from "@/components/shared/BaseFormField.vue";
import NextButton from "@/components/shared/NextButton.vue";
import useFormatter from "@/use/formatter";
import useChecklistHelpers from "@/use/checklistHelpers";
import useValidation from "@/use/validation";
import Tile from "@/components/shared/Tile.vue";
import UpdateIncomeSourcesError from "@/components/errors/UpdateIncomeSourcesError.vue";
import useApplicationData from "@/composables/useApplicationData";

const store = useStore();
const route = useRoute();

const props = defineProps({
  applicant: Object as PropType<Applicant>,
  application: Object as PropType<Application>,
});

const emit = defineEmits(["incomeSourcesUpdated"]);

const { replaceObjectWithId } = useChecklistHelpers();
const { formatName } = useFormatter();
const { validate } = useValidation();

const updatingIncomeSources = ref<boolean>(false);
const showIncomeUploadSection = ref<boolean | undefined>(false);
const editSelection = ref<boolean>(false);
const showEmploymentSurveyQuestions = ref<boolean>(false);
const gettingData = ref<boolean>(true);
const employmentStatusResponse = ref<undefined | string>(undefined);
const employerNameResponse = ref<undefined | string>(undefined);
const updateIncomeSourcesError = ref<boolean>(false);
const paycheckFromEmployerChecked = ref<boolean>(false);

// Computed
const { emailAddress, productTypeId } = useApplicationData();

const employmentSurveyQuestionsEnabled = computed<boolean>(() => {
  const productIds = [ProductIds.REFI, ProductIds.PEL, ProductIds.LOC];
  return productIds.includes(productTypeId.value);
});

const path = computed(() => {
  return store.state.appData?.aplication?.metadata?.withCoApplicants === true ||
    store.state.appData?.applicants?.length > 1
    ? "Co-Applicant"
    : "Primary Applicant";
});

const assignedApplicant = computed(() => {
  return store.state.appData.applicants.find((a: Applicant) => a.applicantTypeName === path.value);
});

const surveyResponses = computed(() => {
  return assignedApplicant.value?.surveyResponses || [];
});

const employerNameSurveyResponse = computed<SurveyResponse | undefined>(() =>
  surveyResponses.value?.find(
    (s: SurveyResponse) => s.questionId === SurveyQuestionTypeIds.EmployerName,
  ),
);

const oidcUser = computed(() => store.getters["oidcStore/oidcUser"]);
const referenceId = computed(
  () => route?.params?.referenceId || store.state.appData?.aplication?.referenceId,
);
const fullName = computed(() => getApplicantName());
const selectedIncomeSources = computed(() =>
  store.state.incomeSources?.filter((i: any) => i.isChecked),
);

const validEmploymentSurveyQuestionsForm = computed(() => {
  if (
    (info["Paystub"] === true &&
      employmentStatusResponse.value === undefined &&
      employerNameResponse.value === undefined) ||
    (info["Paystub"] === true && employmentStatusResponse.value === "") ||
    (employmentStatusResponse.value === "Yes" &&
      (employerNameResponse.value === undefined ||
        employerNameResponse.value === null ||
        (employerNameResponse.value && employerNameResponse.value?.trim()?.length <= 0)))
  ) {
    return false;
  } else {
    return true;
  }
});

let checkboxfieldSchema = reactive({} as any);
let incomeSources = reactive([] as any);
let info = reactive({} as any);
let selectedSources = reactive([] as any[]);
let selectedSourceIds = reactive([] as any[]);
let alreadySelectedIds = reactive([] as number[]);

const employmentStatusSchema = reactive<FieldSchema>({
  type: "select",
  label: "Are you currently employed?",
  hideLabel: false,
  required: false,
  validate(schema: FieldSchema, value: string, compare: any) {
    if (value !== undefined) {
      if (value === "No") {
        schema.valid = true;
        const error = `Current employment is required to qualify for this loan program. Please contact our team to discuss at ${emailAddress.value}.`;
        schema.errors = [error];
      } else if (value === "") {
        const defaultErrorMessage = `${schema.label} is required.`;
        schema.valid = false;
        schema.errors = [defaultErrorMessage];
      } else {
        schema.valid = true;
        schema.errors = [];
      }
    } else {
      const defaultErrorMessage = `${schema.label} is required.`;
      schema.valid = false;
      schema.errors = [defaultErrorMessage];
    }
  },
});

const employerNameSchema = reactive<FieldSchema>({
  type: "text",
  label: "What is the name of your employer?",
  hideLabel: false,
  required: false,
});

const employmentStatusOptions = [
  {
    label: "Yes",
    value: "Yes",
  } as Option,
  {
    label: "No",
    value: "No",
  } as Option,
] as Option[];

function getApplicantName() {
  let fullName = "";
  if (assignedApplicant.value?.id && assignedApplicant.value?.id > 0) {

    const firstName = formatName(assignedApplicant.value?.firstName?.toLowerCase());
    let lastName = formatName(assignedApplicant.value?.lastName?.toLowerCase());
   
    if(!lastName.endsWith("'s")){
      lastName += "'s";
    }

    fullName = `${firstName} ${lastName}`;
  }
  
  return fullName;
}

function setEmploymentQuestionSurveyResponses() {
  if (surveyResponses.value?.length > 0) {
    const employmentStatus =
      surveyResponses.value?.find(
        (q: SurveyResponseViewModel) => q.questionId === SurveyQuestionTypeIds.EmploymentStatus,
      )?.response || undefined;

    const employerName =
      surveyResponses?.value?.find(
        (q: SurveyResponseViewModel) => q.questionId === SurveyQuestionTypeIds.EmployerName,
      )?.response || undefined;

    employmentStatusResponse.value = employmentStatus;
    employerNameResponse.value = employerName;

    if (employmentStatusResponse.value === "No") {
      validate(employmentStatusSchema, employmentStatusResponse.value);
      employerNameResponse.value = undefined;
    }
  }
}

function setIncomeSources(updatedSources: any) {
  // reset selectedSources
  selectedSources = [];
  selectedSourceIds = [];
  alreadySelectedIds = [];

  updatedSources.forEach((updatedSource: any) => {
    const newItem =
      incomeSources?.find((incomeSource: any) => incomeSource.id === updatedSource.id) ===
      undefined;
    if (newItem === false) {
      replaceObjectWithId(incomeSources, updatedSource.id, updatedSource);
    } else {
      incomeSources.push(updatedSource);
    }

    info[updatedSource.name] = updatedSource.isChecked;

    if (updatedSource.isChecked) {
      selectedSources.push(updatedSource);
      selectedSourceIds.push(updatedSource.id);
      alreadySelectedIds.push(updatedSource.id);

      // failsafe to remove duplicates
      selectedSources = [...new Set(selectedSources)];
      selectedSourceIds = [...new Set(selectedSourceIds)];
      alreadySelectedIds = [...new Set(alreadySelectedIds)];
    }

    showIncomeUploadSection.value = selectedSourceIds?.length > 0;

    checkboxfieldSchema[updatedSource.name] = {
      type: "checkbox",
      label: updatedSource.title,
      required: false,
      hideLabel: true,
      hintPosition: "bottom",
      ...updatedSource,
    };
  });

  if (
    alreadySelectedIds.includes(IncomeSourceTypeIds.PAYCHECK_FROM_EMPLOYER) &&
    employmentSurveyQuestionsEnabled.value
  ) {
    paycheckFromEmployerChecked.value = true;
    setEmploymentQuestionSurveyResponses();
  }

  editSelection.value = false;
  gettingData.value = false;
}
function handleIncomeSourceSelect(event: any, option: any) {
  if (option.id === IncomeSourceTypeIds.PAYCHECK_FROM_EMPLOYER) {
    paycheckFromEmployerChecked.value = event;
  }
}

async function getIncomeSources() {
  gettingData.value = true;
  const sources = await $api.applicant.getIncomeSources(referenceId.value);

  if (sources && sources.length > 0) {
    await store.dispatch("setIncomeSources", sources);
    setIncomeSources(sources);
  } else {
    gettingData.value = false;

    console.log("Error getIncomeSources():", sources);
  }
}
async function updateIncomeSources() {
  updatingIncomeSources.value = true;
  updateIncomeSourcesError.value = false;

  if (employmentSurveyQuestionsEnabled.value && paycheckFromEmployerChecked.value) {
    validate(employmentStatusSchema, employmentStatusResponse.value);
    validate(employerNameSchema, employerNameResponse.value);
  }

  const valid =
    employmentSurveyQuestionsEnabled.value && paycheckFromEmployerChecked.value
      ? validEmploymentSurveyQuestionsForm.value
      : true;

  if (valid) {
    handleIncomeSourceSelection();

    let applicantIncomeSourcesRequest = {
      referenceId: referenceId.value,
      userId: oidcUser?.value?.sub,
      incomeSourceTypeIds: selectedSourceIds,
    } as ApplicantIncomeSourcesRequest;

    if (employmentSurveyQuestionsEnabled.value && paycheckFromEmployerChecked.value) {
      if (typeof employmentStatusResponse.value == "string") {
        applicantIncomeSourcesRequest.currentlyEmployed = employmentStatusResponse.value;
        applicantIncomeSourcesRequest.employerName = employerNameResponse.value;
      }
    }

    const response = await $api.applicant.updateIncomeSources(applicantIncomeSourcesRequest);

    if (response?.length > 0) {
      store.dispatch("setIncomeSources", response);

      // Get latest survey responses
      if (
        showEmploymentSurveyQuestions.value &&
        applicantIncomeSourcesRequest.incomeSourceTypeIds.includes(
          IncomeSourceTypeIds.PAYCHECK_FROM_EMPLOYER,
        )
      ) {
        await store.dispatch("getApplicantSurveyResponses", assignedApplicant.value?.id);
      }

      // updates UI
      incomeSources = response;
      setIncomeSources(incomeSources);

      // exit out of edit mode
      editSelection.value = false;

      updatingIncomeSources.value = false;

      // get latest stips
      emit("incomeSourcesUpdated");
    } else {
      updatingIncomeSources.value = false;
      updateIncomeSourcesError.value = true;
      console.log("Error updateIncomeSources():", response);
    }
  } else {
    updatingIncomeSources.value = false;
  }
}

function getIncomeSourceId(str: string) {
  return incomeSources?.find((i: any) => i.name === str)?.id;
}

function handleIncomeSourceSelection() {
  let sourceIds = [] as number[];
  let checked = [] as string[];
  for (const [key, value] of Object.entries(info)) {
    // console.log(`key: ${key}, value: ${value}`);
    // if checkbox checked, add to selected denial reasons
    if (value === true) {
      checked.push(key);
      sourceIds.push(getIncomeSourceId(key));
    }
  }
  // failsafe to remove duplicates
  selectedSources = [...new Set(checked)];
  selectedSourceIds = [...new Set(sourceIds)];
}

function resetSelection() {
  editSelection.value = true;
  selectedSources = [];
  selectedSourceIds = [];

  showIncomeUploadSection.value = true;
}

function toggleIncomeSelector() {
  if (
    editSelection.value ||
    (showIncomeUploadSection.value && selectedIncomeSources.value?.length <= 0)
  ) {
    cancel();
  } else {
    showIncomeUploadSection.value = !showIncomeUploadSection.value;
  }
}

function cancel() {
  // discard changes
  setIncomeSources(store.state.incomeSources);

  // exit edit mode
  editSelection.value = false;
}

function toggleIncomeVerificationSection() {
  showIncomeUploadSection.value = true;
}

function resetSelectedIncomeSources() {
  for (let [key, value] of Object.entries(info)) {
    info[key] = false;
  }
}

function resetEmploymentSurveyQuestionsForm() {
  employmentStatusSchema.required = false;
  employerNameSchema.required = false;
  employmentStatusSchema.touched = false;
  employerNameSchema.touched = false;
  employmentStatusSchema.valid = undefined;
  employerNameSchema.valid = undefined;
  employerNameResponse.value = undefined;
  employmentStatusResponse.value = undefined;
  employmentStatusSchema.errors = [];
  employerNameSchema.errors = [];
}

watch(
  () => info["Paystub"],
  value => {
    if (typeof value === "boolean") {
      if (employmentSurveyQuestionsEnabled.value) {
        showEmploymentSurveyQuestions.value = value;
        employmentStatusSchema.required = value;
      }

      if (value === false) {
        resetEmploymentSurveyQuestionsForm();
      }
    }
  },
);

watch(
  () => employmentStatusResponse.value,
  value => {
    if (value === "Yes") {
      employerNameSchema.required = true;
    } else {
      employerNameResponse.value = undefined;
      employerNameSchema.required = false;
    }
  },
  { immediate: true },
);

watch(
  () => employerNameSurveyResponse.value,
  value => {
    employerNameResponse.value = value?.response;
  },
);

onMounted(async () => {
  getIncomeSources();
});
</script>
<style scoped lang="scss">
.income-verification-tile-wrapper {
  // border: 1px solid lightgray;
  // padding: 1em;
  // margin-bottom: 1em;
  // background-color: #eeeeee;
}

.income-verification-tile {
  .income-verification-tile-header {
    display: flex;

    .col-left {
      flex-basis: 70%;
      flex-direction: column;
    }

    .col-right {
      margin-left: auto;
      padding-right: 0;
    }

    .title {
      font-weight: 700;
      font-size: 1.25rem;
    }

    .action {
      color: #767676;
      font-size: 1rem;
      cursor: pointer;
    }

    .caption {
      font-weight: normal;
      font-size: 1rem;
    }
  }

  .income-verification-tile-content {
    .selected-income-sources {
      .selected-income-sources-title {
        margin-bottom: 0;
        font-weight: 700;
      }

      ul.selected-options {
        li.selected-option {
          list-style: square;
        }
      }

      a {
        font-weight: 700;
        text-decoration: underline;
        cursor: pointer;
      }
    }
  }
}

.employment-survey-questions-wrapper {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  margin-bottom: 1rem;
}
</style>
